import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch, type Control, type FieldErrors, type UseFormSetValue, type UseFormGetValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box, Typography,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createPurchaseOrder, updatePurchaseOrder, getPurchaseOrder, getSuggestedPrice,
  type PurchaseOrderListItem, type SavePurchaseOrderPayload,
} from '../../api/comenziFurnizor'
import { listSuppliers } from '../../api/furnizori'
import { listWarehouses } from '../../api/depozite'
import { listArticles, listUnits, type ArticleListItem, type UnitOfMeasure } from '../../api/nomenclatoare'
import { listVatRates, type VatRate } from '../../api/taxe'
import { listContracts } from '../../api/contracte'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  quantity: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().min(1, 'Selectați o UM'),
  unitPrice: z.string().optional(),
  vatRateId: z.string().nullable(),
})
const schema = z.object({
  supplierId: z.string().min(1, 'Selectați un furnizor'),
  deliveryWarehouseId: z.string().min(1, 'Selectați un depozit'),
  contractId: z.string().nullable(),
  requestedDeliveryDate: z.string().min(1, 'Câmp obligatoriu'),
  paymentTerms: z.string().optional(),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  transactionType: z.enum(['Local', 'Intracomunitar', 'Import']),
  notes: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  supplierId: '', deliveryWarehouseId: '', contractId: null, requestedDeliveryDate: '', paymentTerms: '',
  currency: 'RON', transactionType: 'Local', notes: '', lines: [],
}

const SOURCE_LABEL: Record<string, string> = { 'Preț contract': 'contract', 'Preț negociat furnizor': 'preț negociat' }

function LineRow({
  index, control, errors, articles, units, vatRates, supplierId, contractId, currency, setValue, getValues, onRemove,
}: {
  index: number
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  articles: ArticleListItem[] | undefined
  units: UnitOfMeasure[] | undefined
  vatRates: VatRate[] | undefined
  supplierId: string
  contractId: string | null
  currency: string
  setValue: UseFormSetValue<FormValues>
  getValues: UseFormGetValues<FormValues>
  onRemove: () => void
}) {
  const articleId = useWatch({ control, name: `lines.${index}.articleId` })

  const { data: suggested } = useQuery({
    queryKey: ['achizitii', 'comenzi', 'pret-sugerat', supplierId, articleId, contractId],
    queryFn: () => getSuggestedPrice(supplierId, articleId, contractId),
    enabled: !!supplierId && !!articleId,
  })

  useEffect(() => {
    if (!articleId) return
    const article = articles?.find((a) => a.id === articleId)
    if (article?.vatRateId && !getValues(`lines.${index}.vatRateId`)) {
      setValue(`lines.${index}.vatRateId`, article.vatRateId)
    }
  }, [articleId, articles, index, getValues, setValue])

  useEffect(() => {
    const currentPrice = getValues(`lines.${index}.unitPrice`)
    if (suggested?.price != null && (!currentPrice || Number(currentPrice) === 0)) {
      setValue(`lines.${index}.unitPrice`, String(suggested.price))
    }
  }, [suggested, index, getValues, setValue])

  const sourceLabel = suggested?.price != null ? SOURCE_LABEL[suggested.source] ?? suggested.source : null
  const currencyMismatch = suggested?.currency && suggested.currency !== currency

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Controller name={`lines.${index}.articleId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
          renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[index]?.articleId} helperText={errors.lines?.[index]?.articleId?.message} />}
        />
      )} />
      <Controller name={`lines.${index}.quantity`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Cantitate" sx={{ width: 120 }} error={!!errors.lines?.[index]?.quantity} helperText={errors.lines?.[index]?.quantity?.message} />
      )} />
      <Controller name={`lines.${index}.unitOfMeasureId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 110 }} options={units?.map((u) => u.id) ?? []}
          getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
          value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
          renderInput={(params) => <TextField {...params} label="UM" error={!!errors.lines?.[index]?.unitOfMeasureId} helperText={errors.lines?.[index]?.unitOfMeasureId?.message} />}
        />
      )} />
      <Box sx={{ width: 170 }}>
        <Controller name={`lines.${index}.unitPrice`} control={control} render={({ field }) => (
          <TextField {...field} type="number" label="Preț (auto dacă gol)" fullWidth />
        )} />
        {sourceLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Sugerat din {sourceLabel}{currencyMismatch ? ` (${suggested?.currency})` : ''}
          </Typography>
        )}
      </Box>
      <Controller name={`lines.${index}.vatRateId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 140 }} options={vatRates?.map((v) => v.id) ?? []}
          getOptionLabel={(id) => { const v = vatRates?.find((x) => x.id === id); return v ? `${v.name} (${v.ratePercent}%)` : id }}
          value={field.value} onChange={(_, v) => field.onChange(v)}
          renderInput={(params) => <TextField {...params} label="TVA" />}
        />
      )} />
      <IconButton onClick={onRemove} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
    </Stack>
  )
}

export default function PurchaseOrderFormDialog({ target, onClose }: { target: PurchaseOrderListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'taxe', 'cote-tva'], queryFn: listVatRates, enabled: open })
  const { data: contracts } = useQuery({ queryKey: ['achizitii', 'contracte'], queryFn: listContracts, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'comenzi', editing ? (target as PurchaseOrderListItem).id : null],
    queryFn: () => getPurchaseOrder((target as PurchaseOrderListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const supplierId = useWatch({ control, name: 'supplierId' })
  const contractId = useWatch({ control, name: 'contractId' })
  const currency = useWatch({ control, name: 'currency' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        supplierId: detail.supplierId, deliveryWarehouseId: detail.deliveryWarehouseId, contractId: detail.sourceContractId,
        requestedDeliveryDate: detail.requestedDeliveryDate, paymentTerms: detail.paymentTerms ?? '',
        currency: detail.currency, transactionType: detail.transactionType, notes: detail.notes ?? '',
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, quantity: String(l.quantity), unitOfMeasureId: l.unitOfMeasureId,
          unitPrice: String(l.unitPrice), vatRateId: l.vatRateId,
        })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const supplierContracts = (contracts ?? []).filter((c) => c.status === 'Activ' && c.supplierId === supplierId)

  useEffect(() => {
    if (!contractId || !contracts) return
    const selected = contracts.find((c) => c.id === contractId)
    if (selected && selected.supplierId !== supplierId) {
      setValue('contractId', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, contracts])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SavePurchaseOrderPayload = {
        supplierId: values.supplierId, deliveryWarehouseId: values.deliveryWarehouseId,
        requestedDeliveryDate: values.requestedDeliveryDate, paymentTerms: values.paymentTerms || null,
        currency: values.currency, transactionType: values.transactionType, notes: values.notes || null,
        sourceContractId: values.contractId,
        lines: values.lines.map((l) => ({
          articleId: l.articleId, quantity: Number(l.quantity), unitOfMeasureId: l.unitOfMeasureId,
          unitPrice: l.unitPrice ? Number(l.unitPrice) : null, vatRateId: l.vatRateId,
        })),
      }
      if (editing) return updatePurchaseOrder((target as PurchaseOrderListItem).id, payload)
      return createPurchaseOrder(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'comenzi'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare comandă furnizor' : 'Comandă furnizor nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="supplierId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={suppliers?.map((s) => s.id) ?? []}
                  getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Furnizor" error={!!errors.supplierId} helperText={errors.supplierId?.message} />}
                />
              )} />
              <Controller name="deliveryWarehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit de livrare" error={!!errors.deliveryWarehouseId} helperText={errors.deliveryWarehouseId?.message} />}
                />
              )} />
              <Controller name="contractId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={supplierContracts.map((c) => c.id)}
                  getOptionLabel={(id) => supplierContracts.find((c) => c.id === id)?.number ?? id}
                  value={field.value} onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => <TextField {...params} label="Contract (opțional)" />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="requestedDeliveryDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Livrare solicitată" sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.requestedDeliveryDate} helperText={errors.requestedDeliveryDate?.message} />
              )} />
              <Controller name="paymentTerms" control={control} render={({ field }) => (
                <TextField {...field} label="Termeni de plată" sx={{ flexGrow: 1 }} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 100 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
              <Controller name="transactionType" control={control} render={({ field }) => (
                <TextField {...field} select label="Tip achiziție" sx={{ width: 180 }} error={!!errors.transactionType} helperText={errors.transactionType?.message}>
                  <MenuItem value="Local">Locală</MenuItem>
                  <MenuItem value="Intracomunitar">Intracomunitară</MenuItem>
                  <MenuItem value="Import">Import</MenuItem>
                </TextField>
              )} />
            </Stack>
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <LineRow
                key={f.id} index={i} control={control} errors={errors} articles={articles} units={units} vatRates={vatRates}
                supplierId={supplierId} contractId={contractId} currency={currency} setValue={setValue} getValues={getValues}
                onRemove={() => lines.remove(i)}
              />
            ))}
            <Box>
              <Button size="small" startIcon={<Add />}
                onClick={() => lines.append({ articleId: '', quantity: '', unitOfMeasureId: '', unitPrice: '', vatRateId: null })}>
                Adaugă linie
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
